import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import PageHeader from "@/components/ui/page-header";
import Table from "@/components/ui/table";
import Chip from "@/components/ui/chip";
import Popover from "@/components/ui/popover";
import ConfirmModal from "@/components/ui/confirm-modal";
import {
  Actions as ActionsIcon,
  Eye as EyeIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  CardView as CardViewIcon,
  Tickets as TicketsIcon,
} from "@/components/icons";
import type { FilterField, TableColumn, SortOption } from "@/types/component";
import type {
  Education,
  EducationStatus,
  EducationFormat,
  TargetAudience,
  EducationDetailApi,
} from "../types/education-management.types";
import { educationManagementApi } from "../services/education-management.api";
import EducationDetailDrawer from "../components/EducationDetailDrawer";
import VirtualClassroomsListModal from "@/features/request-and-approval/components/VirtualClassroomsListModal";
import NewClientSupportTicketModal from "@/features/client-support-tickets/components/NewClientSupportTicketModal";
import EducationSupportTicketsDrawer from "@/features/support-tickets/components/EducationSupportTicketsDrawer";
import { usePermission } from "@/core/permissions/hooks";
import { PERMISSIONS } from "@/core/permissions/config";
import { useStore } from "@/store";
import { UserRoleType } from "@/features/roles-and-permissions/types/roles-and-permissions.types";
import { isEducationManagerSession } from "@/core/auth/utils/education-manager-session";
import { isEducationOfficerSession } from "@/core/auth/utils/education-officer-session";
import { isTrainerSession } from "@/core/auth/utils/trainer-session";
import styles from "./EducationManagementPage.module.scss";

// Status configurations
const STATUS_TYPES: Record<
  EducationStatus,
  "success" | "warning" | "error" | "info" | "default"
> = {
  aktif: "success",
  talepToplama: "warning",
  kapali: "default",
  tamamlandi: "info",
  butceBekliyor: "warning",
  taslak: "error",
};

// Format keys for i18n (educationManagement.formatOptions)
const FORMAT_I18N_KEYS: Record<EducationFormat, string> = {
  uniteli: "uniteli",
  canli: "canli",
  yuzYuze: "yuzYuze",
  nonUnit: "nonUnit",
};

// Target audience keys for i18n (createEducation.basicInfo.targetAudienceOptions)
const TARGET_AUDIENCE_I18N_KEYS: Record<TargetAudience, string> = {
  1: "branch",
  0: "central",
  2: "all",
};

const SEARCH_DEBOUNCE_MS = 400;

const EducationManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userType = useStore((s) => s.userType);
  const currentUser = useStore((s) => s.currentUser);
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(PERMISSIONS.EDUCATION_CREATE);
  const canUpdate = hasPermission(PERMISSIONS.EDUCATION_UPDATE);
  const canDelete = hasPermission(PERMISSIONS.EDUCATION_DELETE);
  const canCreateSupportTicket =
    hasPermission(PERMISSIONS.SUPPORT_TICKETS_CREATE) &&
    (isEducationManagerSession(userType, currentUser) ||
      isEducationOfficerSession(userType, currentUser) ||
      isTrainerSession(userType, currentUser));
  const canViewSupportTickets =
    hasPermission(PERMISSIONS.SUPPORT_TICKETS_VIEW) &&
    isEducationManagerSession(userType, currentUser);

  const [educations, setEducations] = useState<Education[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedRows, setSelectedRows] = useState<Education[]>([]);
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>(
    {},
  );
  const [filterValues, setFilterValues] = useState<Record<string, any>>(() => {
    // URL'den (örn. dashboard kartı ile ?status=2) ilk filtreleri al
    const initial: Record<string, any> = {};
    const s = searchParams.get("status");
    if (s) initial.status = s;
    return initial;
  });
  const [sortBy, setSortBy] = useState<
    | "publishedAt"
    | "requestedEnrollmentsCount"
    | "createdAt"
    | "duration"
    | "requestCollectionEndDate"
  >("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false);
  const [selectedEducationDetail, setSelectedEducationDetail] =
    useState<EducationDetailApi | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [deleteModalEducation, setDeleteModalEducation] =
    useState<Education | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [exportingEducationId, setExportingEducationId] = useState<string | null>(
    null,
  );
  const [virtualClassroomsEducation, setVirtualClassroomsEducation] =
    useState<Education | null>(null);
  const [supportTicketEducation, setSupportTicketEducation] =
    useState<Education | null>(null);
  const [supportTicketsListEducation, setSupportTicketsListEducation] =
    useState<Education | null>(null);

  // Filter options (categories, instructors) for dropdowns
  const [categoryOptions, setCategoryOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [instructorOptions, setInstructorOptions] = useState<
    { label: string; value: string }[]
  >([]);

  // Fetch categories and instructors for filter dropdowns
  useEffect(() => {
    educationManagementApi
      .getCategories()
      .then((list) => {
        setCategoryOptions(
          list.map((c) => ({ label: c.name ?? c.id, value: c.id })),
        );
      })
      .catch(() => setCategoryOptions([]));
    educationManagementApi
      .getInstructors()
      .then((list) => {
        setInstructorOptions(
          list.map((i) => {
            const id = i.userID ?? i.id;
            const label =
              [i.firstName, i.lastName].filter(Boolean).join(" ") || id;
            return { label, value: id };
          }),
        );
      })
      .catch(() => setInstructorOptions([]));
  }, []);

  // Search debounce: API çağrısı yazmayı bıraktıktan SEARCH_DEBOUNCE_MS sonra yapılır
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch educations
  const fetchEducations = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await educationManagementApi.getAll({
        page: currentPage,
        pageSize,
        sortBy,
        sortOrder,
        filters: {
          search: debouncedSearch || undefined,
          type: filterValues.type,
          status: filterValues.status,
          targetAudience: filterValues.targetAudience,
          format: filterValues.format,
          categoryIds: filterValues.categoryIds,
          instructorIds: filterValues.instructorIds,
          publishDate: filterValues.publishDate,
          isRequired: filterValues.isRequired,
        },
      });
      setEducations(response.data);
      setTotalRecords(response.total);
    } catch (error) {
      console.error("Error fetching educations:", error);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, debouncedSearch, sortBy, sortOrder, filterValues]);

  useEffect(() => {
    fetchEducations();
  }, [fetchEducations]);

  // Handle search (page reset is done in debounce effect)
  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle page size change
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  // Handle row selection
  const handleRowSelect = (row: Education) => {
    setSelectedRows((prev) => {
      const isSelected = prev.some((r) => r.id === row.id);
      if (isSelected) {
        return prev.filter((r) => r.id !== row.id);
      }
      return [...prev, row];
    });
  };

  // Handle select all
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRows(educations);
    } else {
      setSelectedRows([]);
    }
  };

  // Handle dropdown toggle
  const handleDropdownToggle = (rowId: string, isOpen: boolean) => {
    setOpenDropdowns((prev) => ({
      ...prev,
      [rowId]: isOpen,
    }));
  };

  // Handle add new education
  const handleAddNew = () => {
    navigate("/dashboard/education-management/create");
  };

  // Handle filter change
  const handleFilterChange = (filters: Record<string, any>) => {
    setFilterValues(filters);
    setCurrentPage(1);
  };

  // Format date for display: DD.MM.YYYY. createdAt/publishDate'te anlamlı saat
  // tutulmuyor (backend UTC 00:00 kaydediyor), kullanıcıya her satırda "00:00"
  // görünmesi karışıklık yaratıyordu — bu yüzden saati göstermiyoruz.
  const formatDate = (dateString: string) => {
    if (!dateString) return t("common.datePending");
    const date = new Date(dateString);
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${dd}.${mm}.${yyyy}`;
  };

  // Sort options
  const sortOptions: SortOption[] = [
    {
      label: t("educationManagement.sort.publishDateNew"),
      value: "publishDate_new",
    },
    {
      label: t("educationManagement.sort.publishDateOld"),
      value: "publishDate_old",
    },
    {
      label: t("educationManagement.sort.registrationCountHigh"),
      value: "registrationCount_high",
    },
    {
      label: t("educationManagement.sort.registrationCountLow"),
      value: "registrationCount_low",
    },
    {
      label: t("educationManagement.sort.createdAtNew"),
      value: "createdAt_new",
    },
    {
      label: t("educationManagement.sort.createdAtOld"),
      value: "createdAt_old",
    },
  ];

  const handleExportEnrolledParticipants = async (row: Education) => {
    handleDropdownToggle(row.id, false);
    setExportingEducationId(row.id);
    try {
      const { blob, filename } =
        await educationManagementApi.exportEnrolledParticipants(row.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(
        t("educationManagement.exportParticipantsSuccess"),
      );
    } catch {
      toast.error(
        t("educationManagement.exportParticipantsError"),
      );
    } finally {
      setExportingEducationId(null);
    }
  };

  // Handle delete confirm
  const handleConfirmDelete = async () => {
    if (!deleteModalEducation) return;
    setIsDeleting(true);
    try {
      await educationManagementApi.delete(deleteModalEducation.id);
      toast.success(
        t("educationManagement.deleteSuccess"),
      );
      setDeleteModalEducation(null);
      fetchEducations();
    } catch {
      toast.error(
        t("educationManagement.deleteError"),
      );
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle sort change
  const handleSortChange = (option: SortOption) => {
    switch (option.value) {
      case "publishDate_new":
        setSortBy("publishedAt");
        setSortOrder("desc");
        break;
      case "publishDate_old":
        setSortBy("publishedAt");
        setSortOrder("asc");
        break;
      case "registrationCount_high":
        setSortBy("requestedEnrollmentsCount");
        setSortOrder("desc");
        break;
      case "registrationCount_low":
        setSortBy("requestedEnrollmentsCount");
        setSortOrder("asc");
        break;
      case "createdAt_new":
        setSortBy("createdAt");
        setSortOrder("desc");
        break;
      case "createdAt_old":
        setSortBy("createdAt");
        setSortOrder("asc");
        break;
      default:
        setSortBy("createdAt");
        setSortOrder("desc");
    }
    setCurrentPage(1);
  };

  // Filter fields (GET params: type, status, categoryIds, instructorIds)
  const filterFields: FilterField[] = [
    {
      name: "type",
      label: t("educationManagement.filters.educationType"),
      type: "dropdown",
      placeholder: t("educationManagement.filters.educationTypePlaceholder"),
      options: [
        {
          label: t("educationManagement.filters.formatFilterOptions.0"),
          value: "0",
        },
        {
          label: t("educationManagement.filters.formatFilterOptions.1"),
          value: "1",
        },
        {
          label: t("educationManagement.filters.formatFilterOptions.2"),
          value: "2",
        },
        {
          label: t("educationManagement.filters.formatFilterOptions.3"),
          value: "3",
        },
      ],
      searchable: true,
    },
    {
      name: "status",
      label: t("educationManagement.filters.educationStatus"),
      type: "dropdown",
      placeholder: t("educationManagement.filters.educationStatusPlaceholder"),
      options: [
        {
          label: t("educationManagement.filters.statusFilterOptions.0"),
          value: "0",
        },
        {
          label: t("educationManagement.filters.statusFilterOptions.1"),
          value: "1",
        },
        {
          label: t("educationManagement.filters.statusFilterOptions.2"),
          value: "2",
        },
        {
          label: t("educationManagement.filters.statusFilterOptions.3"),
          value: "3",
        },
        {
          label: t("educationManagement.filters.statusFilterOptions.4"),
          value: "4",
        },
      ],
      searchable: true,
    },
    {
      name: "categoryIds",
      label: t("educationManagement.filters.educationCategory"),
      type: "dropdown",
      placeholder: t("educationManagement.filters.educationCategoryPlaceholder"),
      options: categoryOptions,
      searchable: true,
      multiple: true,
    },
    {
      name: "instructorIds",
      label: t("educationManagement.filters.instructor"),
      type: "dropdown",
      placeholder: t("educationManagement.filters.instructorPlaceholder"),
      options: instructorOptions,
      searchable: true,
      multiple: true,
    },
    {
      name: "publishDate",
      label: t("educationManagement.filters.publishDate"),
      type: "daterange",
      placeholder: t("educationManagement.filters.publishDatePlaceholder"),
    },
    {
      name: "isRequired",
      label: t("educationManagement.filters.mandatory"),
      type: "dropdown",
      placeholder: t("educationManagement.filters.mandatoryPlaceholder"),
      options: [
        {
          label: t("educationManagement.filters.mandatoryYes"),
          value: "true",
        },
        {
          label: t("educationManagement.filters.mandatoryNo"),
          value: "false",
        },
      ],
    },
  ];

  // Table columns
  const columns: TableColumn<Education>[] = [
    {
      header: t("educationManagement.columns.educationCode"),
      accessor: (row) => row.educationCode?.trim() || "—",
      className: "education-code-column",
    },
    {
      header: t("educationManagement.columns.educationName"),
      accessor: (row) => row.educationName?.trim() || "—",
      className: "education-name-column",
    },
    {
      header: t("educationManagement.columns.mandatory"),
      accessor: (row) =>
        row.isRequired ? (
          <Chip type="error" variant="filled" size="small">
            {t("educationManagement.filters.mandatoryYes")}
          </Chip>
        ) : (
          <Chip type="default" variant="outlined" size="small">
            {t("educationManagement.filters.mandatoryNo")}
          </Chip>
        ),
      className: "mandatory-column",
    },
    {
      header: t("educationManagement.columns.educatorName"),
      accessor: (row) => row.educatorName?.trim() || "—",
      className: "educator-column",
    },
    {
      header: t("educationManagement.columns.format"),
      accessor: (row) =>
        t(`educationManagement.formatOptions.${FORMAT_I18N_KEYS[row.format]}`),
      className: "format-column",
    },
    {
      header: t("educationManagement.columns.targetAudience"),
      accessor: (row) =>
        t(
          `createEducation.basicInfo.targetAudienceOptions.${TARGET_AUDIENCE_I18N_KEYS[row.targetAudience]}`,
        ),
      className: "target-audience-column",
    },
    {
      header: t("educationManagement.columns.registrationCount"),
      accessor: (row) =>
        row.maxRegistration
          ? `${row.registrationCount} / ${row.maxRegistration}`
          : row.registrationCount.toString(),
      className: "registration-column",
    },
    {
      header: t("educationManagement.columns.publishDate"),
      accessor: (row) => formatDate(row.publishDate),
      className: "date-column",
    },
    {
      header: t("educationManagement.columns.createdAt"),
      accessor: (row) => formatDate(row.createdAt),
      className: "date-column",
    },
    {
      header: t("educationManagement.columns.status"),
      accessor: (row) => {
        // Sadece canlı (online) aktif eğitimleri "Sanal Sınıf" olarak etiketle.
        // Üniteli eğitimlerde sınıf kavramı yok, bu yüzden dışarıda bırakıldı.
        const isVirtualClassroom =
          row.status === "aktif" && row.format === "canli";
        const chipType = isVirtualClassroom ? "info" : STATUS_TYPES[row.status];
        const label = isVirtualClassroom
          ? t("educationManagement.statusOptions.sanalSinif")
          : t(`educationManagement.statusOptions.${row.status}`);
        return (
          <Chip type={chipType} variant="outlined" size="small">
            {label}
          </Chip>
        );
      },
      className: "status-column",
    },
  ];

  // Render actions
  const renderActions = (row: Education) => {
    const rowId = row.id;
    const isOpen = openDropdowns[rowId] || false;

    const menuItems = [
      {
        label: t("educationManagement.actions.view"),
        icon: <EyeIcon width={20} height={20} />,
        onClick: () => {
          handleDropdownToggle(rowId, false);
          setSelectedEducationDetail(null);
          setDetailLoading(true);
          setIsDetailDrawerOpen(true);
          educationManagementApi
            .getById(row.id)
            .then((res) => {
              setSelectedEducationDetail(res.data);
            })
            .catch(() => {
              setSelectedEducationDetail(null);
            })
            .finally(() => {
              setDetailLoading(false);
            });
        },
      },
      {
        label: t("educationManagement.actions.virtualClassrooms"),
        icon: <CardViewIcon width={20} height={20} />,
        onClick: () => {
          handleDropdownToggle(rowId, false);
          setVirtualClassroomsEducation(row);
        },
      },
      userType === UserRoleType.EDUCATION_MANAGER && {
        label: t("educationManagement.actions.downloadParticipants"),
        icon: <DownloadIcon width={20} height={20} />,
        onClick: () => handleExportEnrolledParticipants(row),
      },
      canCreateSupportTicket && {
        label: t("educationManagement.actions.createSupportTicket"),
        icon: <TicketsIcon width={20} height={20} />,
        onClick: () => {
          handleDropdownToggle(rowId, false);
          setSupportTicketEducation(row);
        },
      },
      canViewSupportTickets && {
        label: t("educationManagement.actions.viewSupportTickets"),
        icon: <TicketsIcon width={20} height={20} />,
        onClick: () => {
          handleDropdownToggle(rowId, false);
          setSupportTicketsListEducation(row);
        },
      },
      canUpdate && {
        label: t("educationManagement.actions.edit"),
        icon: <EditIcon width={20} height={20} />,
        onClick: () => {
          handleDropdownToggle(rowId, false);
          navigate(`/dashboard/education-management/${row.id}/edit`);
        },
      },
      canDelete && {
        label: t("educationManagement.actions.delete"),
        icon: <DeleteIcon width={20} height={20} />,
        onClick: () => {
          setDeleteModalEducation(row);
          handleDropdownToggle(rowId, false);
        },
        color: "error" as const,
      },
    ].filter(Boolean) as {
      label: string;
      icon: React.ReactNode;
      onClick: () => void;
      color?: "error";
    }[];

    const menuContent = (
      <div className={styles.actionMenu}>
        {menuItems.map((item, index) => (
          <button
            key={index}
            className={`${styles.actionMenuItem} ${item.color === "error" ? styles.error : ""}`}
            disabled={exportingEducationId === row.id}
            onClick={(e) => {
              e.stopPropagation();
              item.onClick();
            }}
          >
            {item.icon}
            {item.label}
          </button>
        ))}
      </div>
    );

    return (
      <Popover
        content={menuContent}
        isOpen={isOpen}
        onClose={() => handleDropdownToggle(rowId, false)}
        position="bottom" // position="bottom"
      >
        <button
          className={styles.actionButton}
          onClick={(e) => {
            e.stopPropagation();
            handleDropdownToggle(rowId, !isOpen);
          }}
        >
          <ActionsIcon width={20} height={20} />
        </button>
      </Popover>
    );
  };

  return (
    <DashboardLayout>
      <Header title={t("educationManagement.title")} />

      <PageHeader
        searchPlaceholder={t("educationManagement.searchPlaceholder")}
        searchValue={searchTerm}
        onSearch={handleSearch}
        showSort
        sortOptions={sortOptions}
        onSort={handleSortChange}
        filters={filterFields}
        onFilterChange={handleFilterChange}
        onAddNew={canCreate ? handleAddNew : undefined}
        addButtonLabel={
          canCreate
            ? t("educationManagement.addButton")
            : undefined
        }
      />

      <Table
        columns={columns}
        data={educations}
        isLoading={isLoading}
        selectedRows={selectedRows}
        onRowSelect={handleRowSelect}
        onSelectAll={handleSelectAll}
        getRowId={(row) => row.id}
        currentPage={currentPage}
        pageSize={pageSize}
        totalRecords={totalRecords}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        renderActions={renderActions}
        openDropdowns={openDropdowns}
        emptyMessage={t("educationManagement.noData")}
        onRowClick={(row) => {
          // Atanmamış EDUCATION_SUPERVISOR satır tıklamasında /unauthorized'a
          // düşmesin — edit sayfasına sadece global UPDATE yetkisi olanlar veya
          // bu eğitime per-education yönetici olarak atananlar gidebilir.
          const canOpenEdit =
            canUpdate || row.isCurrentUserManager === true;
          if (!canOpenEdit) return;
          navigate(`/dashboard/education-management/${row.id}/edit`);
        }}
      />

      {/* Education Detail Drawer */}
      <EducationDetailDrawer
        isOpen={isDetailDrawerOpen}
        onClose={() => {
          setIsDetailDrawerOpen(false);
          setSelectedEducationDetail(null);
        }}
        educationDetail={selectedEducationDetail}
        isLoading={detailLoading}
      />

      <NewClientSupportTicketModal
        isOpen={supportTicketEducation !== null}
        onClose={() => setSupportTicketEducation(null)}
        onSuccess={() => {
          toast.success(t("clientSupportTickets.newTicket.toastSuccess"));
        }}
        educationId={supportTicketEducation?.id}
        educationName={supportTicketEducation?.educationName}
      />

      <EducationSupportTicketsDrawer
        isOpen={supportTicketsListEducation !== null}
        onClose={() => setSupportTicketsListEducation(null)}
        educationId={supportTicketsListEducation?.id ?? ""}
        subtitle={
          supportTicketsListEducation
            ? `${supportTicketsListEducation.educationCode} — ${supportTicketsListEducation.educationName}`
            : undefined
        }
      />

      <VirtualClassroomsListModal
        isOpen={virtualClassroomsEducation !== null}
        onClose={() => setVirtualClassroomsEducation(null)}
        educationId={virtualClassroomsEducation?.id ?? ""}
        subtitle={
          virtualClassroomsEducation
            ? `${virtualClassroomsEducation.educationCode} — ${virtualClassroomsEducation.educationName}`
            : undefined
        }
      />

      {/* Delete confirmation modal */}
      <ConfirmModal
        isOpen={!!deleteModalEducation}
        onClose={() => setDeleteModalEducation(null)}
        onConfirm={handleConfirmDelete}
        title={t("educationManagement.deleteModal.title")}
        message={
          deleteModalEducation
            ? t(
                "educationManagement.deleteModal.message",
                `"${deleteModalEducation.educationName}" eğitimini silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`,
              )
            : ""
        }
        confirmText={t("educationManagement.deleteModal.confirm")}
        cancelText={t("common.cancel")}
        loading={isDeleting}
        confirmVariant="danger"
      />
    </DashboardLayout>
  );
};

export default EducationManagementPage;
